/* eslint-disable no-nested-ternary */
import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import styled from '@emotion/styled';
import DraggableContent from './DraggableContent';
import { setExpandedItem, removeExpandedItem } from '../../ducks/calendarActions';
import { flushDebounceForTable } from './hooks/debounceHandler';
import useCalendarDnDMapping from './hooks/useCalendarDnDMapping';

const Wrapper = styled('div')`
`;

const DraggableContentWrapper = ({
  recurringWorkout,
  idx,
  beforeOrAfter,
  measure,
  isBeforeExpanded,
  isAfterExpanded,
  date,
  onDayExpand,
  isDayExpanded,
  isAthleteAddedItem,
  athleteAddedItems,
}) => {
  const dispatch = useDispatch();
  const contentRef = useRef(null);
  const [mouseOverRecurringBefore, setMouseOverRecurringBefore] = useState(false);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isExpandedItem = useSelector(
    (state) => state.calendar.data.expandedItems[recurringWorkout.assignedId],
  );
  const isCalendarOptionsDropdownMenuOpen = useSelector(
    (state) => state.calendar.data.isCalendarOptionsDropdownMenuOpen,
  );
  const isStaticAddColumnDropdownMenuopen = useSelector(
    (state) => state.calendar.data.isStaticAddColumnDropdownMenuOpen,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const passedDate = new Date(effectiveDate);
  const endpointFormattedDate = moment(passedDate).format('YYYY-MM-DD');
  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts[endpointFormattedDate],
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts[endpointFormattedDate],
  );

  const readOnlyInheritedRecurringItem = recurringWorkout?.isInherited && !!beforeOrAfter;

  const calendarItems = isAthleteAddedItem
    ? []
    : beforeOrAfter === 'before'
      ? beforeRecurringWorkouts
      : afterRecurringWorkouts;

  const {
    getItemsUnderSessionBreak,
    getSessionBreakAbove,
  } = useCalendarDnDMapping(calendarItems);

  const sessionBreakAbove = useMemo(
    () => getSessionBreakAbove(recurringWorkout),
    [getSessionBreakAbove, recurringWorkout],
  );

  const isSessionBreakAboveExpanded = useSelector(
    (state) => (sessionBreakAbove
      ? !!state.calendar.data.expandedItems[sessionBreakAbove.assignedId]
      : false),
  );

  const shouldCollapseUnderSessionBreak = useMemo(() => {
    if (
      draggedItem?.type === 'session_break'
      && getItemsUnderSessionBreak(draggedItem)?.[recurringWorkout.assignedId]
    ) {
      return true;
    }
    if (
      sessionBreakAbove
      && !sessionBreakAbove.preSave
      && getItemsUnderSessionBreak(sessionBreakAbove)?.[recurringWorkout.assignedId]
      && !isSessionBreakAboveExpanded
    ) {
      return true;
    }
    return false;
  }, [
    draggedItem,
    recurringWorkout,
    sessionBreakAbove,
    isSessionBreakAboveExpanded,
    getItemsUnderSessionBreak,
  ]);

  const doesGroupingLetterAboveItemMatchContent = !!recurringWorkout.groupingLetter
  && recurringWorkout.groupingLetter === calendarItems[idx - 1]?.groupingLetter;

  const doesGroupingLetterBelowItemMatchContent = !!recurringWorkout.groupingLetter
  && recurringWorkout.groupingLetter === calendarItems[idx + 1]?.groupingLetter;
  const handleClick = () => {
    if (!isAthleteAddedItem && !readOnlyInheritedRecurringItem) {
      if (isExpandedItem) {
      /** Item is already expanded, so collapse (remove) */
        dispatch(removeExpandedItem(recurringWorkout));
      } else {
        dispatch(setExpandedItem(recurringWorkout)); /** Item is collapsed, so expand (add) */
      }
    }
  };

  const typeDoesNotExpand = () => {
    if (recurringWorkout.type === 'warm_up' || recurringWorkout.type === 'W' || recurringWorkout.type === 'health' || recurringWorkout.type === 'H' || recurringWorkout.type === 'note' || recurringWorkout.type === 'N' || recurringWorkout.type === 'cooldown' || recurringWorkout.type === 'CD') {
      return true;
    }
    return false;
  };

  const shouldHighlight = (
    (mouseOverRecurringBefore && !isExpandedItem && !recurringWorkout.preSave)
    || (mouseOverRecurringBefore && typeDoesNotExpand(recurringWorkout))
  );

  const handleMouseLeave = () => {
    setMouseOverRecurringBefore(false);
    flushDebounceForTable(recurringWorkout.assignedId);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const isInsideContainer = contentRef.current?.contains(e.target);

      if (isCalendarOptionsDropdownMenuOpen || isStaticAddColumnDropdownMenuopen) return;
      if (!isInsideContainer) {
        flushDebounceForTable(recurringWorkout.assignedId);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isCalendarOptionsDropdownMenuOpen, isStaticAddColumnDropdownMenuopen]);

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      /** skip first run because cell measurer wrapper will get that
       *  and we don't want to run it twice */
      hasMounted.current = true;
      return;
    }
    if (measure) {
      /** I don't think we need to clear the cache here because
       * recurring items are all considered to be in one virtualized row
       */
      /** cache?.clearAll(); */
      measure();
    }
  }, [isBeforeExpanded,
    isAfterExpanded,
    isExpandedItem,
    shouldCollapseUnderSessionBreak,
    isAthleteAddedItem,
  ]);

  return (
    <Wrapper
      isAthleteAddedItem={isAthleteAddedItem}
      onClick={handleClick}
      ref={contentRef}
      onMouseEnter={() => setMouseOverRecurringBefore(!isAthleteAddedItem
        && !readOnlyInheritedRecurringItem)}
      onMouseLeave={() => handleMouseLeave()}
      style={{
        background: mouseOverRecurringBefore && 'rgba(252, 252, 252, 1)',
      }}
    >
      {((beforeOrAfter === 'before' && isBeforeExpanded) || (beforeOrAfter === 'after' && isAfterExpanded)) && !shouldCollapseUnderSessionBreak ? (
        <DraggableContent
          measure={measure}
          isHovered={false}
          content={recurringWorkout}
          index={idx}
          isRecurring={beforeOrAfter}
          isExpanded={!!isExpandedItem}
          setIsExpanded={() => handleClick()}
          checkboxVisible={false}
          shouldShowQuickActions={shouldHighlight
            && !isAthleteAddedItem
            && !readOnlyInheritedRecurringItem}
          beforeOrAfter={beforeOrAfter}
          isDragging={false}
          isWeekView={viewType === 'weekView'}
          onDayExpand={onDayExpand}
          isDayExpanded={isDayExpanded}
          isAthleteAddedItem={isAthleteAddedItem}
          readOnlyInheritedRecurringItem={readOnlyInheritedRecurringItem}
          athleteAddedItems={athleteAddedItems}
          date={date}
        />
      ) : (
        null
      )}
    </Wrapper>
  );
};

DraggableContentWrapper.propTypes = {
  measure: PropTypes.func.isRequired,
  recurringWorkout: PropTypes.instanceOf(Object).isRequired,
  idx: PropTypes.number.isRequired,
  beforeOrAfter: PropTypes.string.isRequired,
  isAfterExpanded: PropTypes.string,
  isBeforeExpanded: PropTypes.bool,
  date: PropTypes.string,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
  isAthleteAddedItem: PropTypes.bool,
  athleteAddedItems: PropTypes.arrayOf(
    PropTypes.shape({}),
  ),
};

DraggableContentWrapper.defaultProps = {
  isBeforeExpanded: false,
  isAfterExpanded: false,
  date: null,
  isDayExpanded: false,
  onDayExpand: () => {},
  isAthleteAddedItem: false,
  athleteAddedItems: [],
};

export default DraggableContentWrapper;
